import React, { useContext, useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faHome, faUsers, faPlus, faInbox, faShoppingCart } from '@fortawesome/free-solid-svg-icons';
import styled from 'styled-components';
import { RedirectTab } from '../../common/utils/helper';
import { AddButton, FooterContainer, IconWrapper, NavItemFooter } from './ScrollVote.styles';
import UserContext from 'Contexts/User';
import AppContext from 'Contexts/AppContext';

type FooterTab = 'home' | 'prediction' | 'friends' | 'add' | 'inbox' | 'store';

// Top indicator that sits at the very top of the footer
const FooterTopIndicator = styled.div<{ $position: number }>`
  position: absolute;
  top: 0;
  left: ${props => props.$position}%;
  transform: translateX(-50%);
  width: 40px;
  height: 3px;
  background: linear-gradient(90deg, #9B4DFF 0%, #6A29DD 100%);
  border-radius: 0 0 3px 3px;
  box-shadow: 0 2px 10px rgba(155, 77, 255, 0.8);
  transition: left 0.3s ease;
  
  @media (max-width: 480px) {
    width: 35px;
    height: 2px;
  }
`;

// Wrap FooterContainer to make it position: relative
const FooterWrapper = styled(FooterContainer)`
  position: fixed;
`;

function ScrollFooter() {
  const navigate = useNavigate();
  const location = useLocation();
  const { userInfo } = useContext(UserContext);
  const { setLogin, setSignup, setLoginRedirectMessage } = useContext(AppContext);
  const [activeTab, setActiveTab] = useState<FooterTab>('home');

  // Get the indicator position based on active tab (percentage from left)
  const getIndicatorPosition = (tab: FooterTab): number => {
    switch (tab) {
      case 'home': return 11;
      case 'prediction': return 11;
      case 'friends': return 30.5;
      case 'add': return 50;
      case 'inbox': return 69.5;
      case 'store': return 90;
      default: return 10;
    }
  };

  // Update active tab based on current route
  useEffect(() => {
    const path = location.pathname;
    if (path === '/' || path === '') {
      setActiveTab('home');
    } else if (path === '/prediction') {
      setActiveTab('prediction');
    }
    else if (path.includes('/profile/share') || path.includes('/friends')) {
      setActiveTab('friends');
    } else if (path.includes('/profile/upload')) {
      setActiveTab('add');
    } else if (path.includes('/profile/notifications')) {
      setActiveTab('inbox');
    } else if (path.includes('/store') || path.includes('/profile/mine')) {
      setActiveTab('store');
    }
  }, [location.pathname]);

  const navigateTo = (path: string, tab: FooterTab) => {
    setActiveTab(tab);
    if (!userInfo?.uid) {
      setLoginRedirectMessage("");
      setLogin(true);
      return;
    }
    RedirectTab(path, navigate);
  };

  // Check if indicator should be shown on current path
  const shouldShowIndicator = (): boolean => {
    const path = location.pathname;
    const allowedPaths = ['/', "/prediction", "/profile/share", '/profile/followers', '/profile/notifications', '/profile/upload'];
    return allowedPaths.includes(path) || path === '';
  };

  return (
    <FooterWrapper>
      {shouldShowIndicator() && <FooterTopIndicator $position={getIndicatorPosition(activeTab)} />}

      <NavItemFooter
        $isActive={activeTab === 'home' || activeTab === 'prediction'}
        onClick={() => navigateTo("/", 'home')}
      >
        <IconWrapper $isActive={activeTab === 'home' || activeTab === 'prediction'}>
          <FontAwesomeIcon icon={faHome} />
        </IconWrapper>
        <span>Home</span>
      </NavItemFooter>

      <NavItemFooter
        $isActive={activeTab === 'friends'}
        onClick={() => navigateTo("/profile/share", 'friends')}
      >
        <IconWrapper $isActive={activeTab === 'friends'}>
          <FontAwesomeIcon icon={faUsers} />
        </IconWrapper>
        <span>Parliaments</span>
      </NavItemFooter>

      <NavItemFooter onClick={() => navigateTo("/profile/upload", 'add')}>
        <AddButton>
          <FontAwesomeIcon icon={faPlus} />
        </AddButton>
      </NavItemFooter>

      <NavItemFooter
        $isActive={activeTab === 'inbox'}
        onClick={() => navigateTo("/profile/notifications", 'inbox')}
      >
        <IconWrapper $isActive={activeTab === 'inbox'}>
          <FontAwesomeIcon icon={faInbox} />
        </IconWrapper>
        <span>Inbox</span>
      </NavItemFooter>

      <NavItemFooter
        $isActive={activeTab === 'store'}
        onClick={() => {
          // navigateTo("/store", 'store')
        }}
      >
        <IconWrapper $isActive={activeTab === 'store'}>
          <FontAwesomeIcon icon={faShoppingCart} />
        </IconWrapper>
        <span>Store</span>
      </NavItemFooter>
    </FooterWrapper>
  );
}

export default ScrollFooter;